import { useQuery } from '@tanstack/react-query';
import { listModulePricesPublic, loadModuleFlagMap } from '@pc/db';
import { useActiveOrg } from './ActiveOrgProvider';

/**
 * Resolves whether the calling user is allowed into the shop SPA shell.
 *
 * History
 * -------
 * v1 only granted access when the active org's `org_module_flags.shop`
 * was true. After the always-on / `is_free` rework
 * (supabase/migrations/20260525140100_billing_module_is_free.sql), the
 * `shop` row is marked free and granted to every org regardless of
 * subscription state. We also relax the "must have an org" gate for
 * free modules: a brand-new signup who hasn't bootstrapped an org yet
 * can still browse the catalog. Actions that fundamentally require an
 * org (cart, checkout, message vendor, place order) gracefully degrade
 * to a "Create a workspace to continue" prompt on the consuming pages.
 *
 * Return shape
 * ------------
 *   { ready: false }                          -- still resolving
 *   { ready: true, allowed: false, ... }      -- not signed in / not free / no org
 *   { ready: true, allowed: true, noOrg: false } -- full access
 *   { ready: true, allowed: true, noOrg: true }  -- browse-only (no org yet)
 *   isFree                                    -- the shop module's is_free bit
 */
export interface ShopAccessResult {
  ready: boolean;
  allowed: boolean;
  /** True when the user is signed in but has no active org bound yet. */
  noOrg: boolean;
  /** True when billing.module_prices.shop.is_free = true. */
  isFree: boolean;
}

const SHOP_MODULE_ID = 'shop';

interface ShopAccessQueryData {
  isFree: boolean;
  orgEnabled: boolean | null;
}

export function useShopAccess(): ShopAccessResult {
  const { activeOrgId, ready: orgReady } = useActiveOrg();

  const accessQuery = useQuery<ShopAccessQueryData>({
    queryKey: ['shop', 'access', activeOrgId ?? 'no-org'],
    queryFn: async () => {
      // Fan out: module_prices is the source of truth for "free",
      // org_module_flags is the source of truth for "this specific
      // org has it on". Both are anon/authenticated-readable.
      const [modules, flagMap] = await Promise.all([
        listModulePricesPublic().catch(() => []),
        activeOrgId ? loadModuleFlagMap(activeOrgId).catch(() => null) : null,
      ]);
      const shopModule = modules.find((m) => m.module_id === SHOP_MODULE_ID);
      return {
        isFree: Boolean(shopModule?.is_free),
        orgEnabled: flagMap ? flagMap.isEnabled(SHOP_MODULE_ID) : null,
      };
    },
    // We always want to fetch the module_prices row even before an org
    // is selected so we know whether to allow browse-only access. The
    // org-specific flag lookup is skipped inside queryFn when no org.
    enabled: orgReady,
    staleTime: 60_000,
  });

  if (!orgReady || accessQuery.isPending) {
    return { ready: false, allowed: false, noOrg: !activeOrgId, isFree: false };
  }

  const isFree = accessQuery.data?.isFree ?? false;
  const orgEnabled = accessQuery.data?.orgEnabled ?? null;
  const noOrg = !activeOrgId;

  // Allowed paths:
  //   1. Org present + shop enabled for that org (covers Pro+ orgs and,
  //      after the is_free migration, every org).
  //   2. No org + shop is_free -- browse-only mode.
  const allowed =
    (Boolean(activeOrgId) && orgEnabled === true) || (noOrg && isFree);

  return { ready: true, allowed, noOrg, isFree };
}
